import { useEffect } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { KeyRound, LogOut, User as UserIcon, X } from "lucide-react";
import { NAV_GROUPS, MOBILE_TABS } from "@/shared/lib/navItems";
import { hasMinRole, ROLE_LABEL_KEY } from "@/shared/auth/permissions";
import { selectUser, useAuth } from "@/shared/auth/store";
import { useLogout } from "@/shared/auth/useLogout";
import { env } from "@/shared/config/env";
import { cn } from "@/shared/lib/cn";
import { Avatar, AvatarFallback } from "@/shared/ui/Avatar";

/**
 * Everything the four tabs don't cover, as a bottom sheet.
 *
 * Reads the same NAV_GROUPS the desktop sidebar renders — including the RBAC
 * filter — so a page can never exist on one and be missing from the other.
 * The three routes already promoted to tabs are dropped here rather than
 * repeated: a user hunting for 경보 will use the tab, and a duplicate entry
 * just makes the sheet longer.
 */
export interface MoreSheetProps {
  open: boolean;
  onClose: () => void;
}

export function MoreSheet({ open, onClose }: MoreSheetProps) {
  const { t } = useTranslation();
  const user = useAuth(selectUser);
  const role = user?.role;
  const navigate = useNavigate();
  const logoutM = useLogout();

  // Esc closes, and the body must not scroll behind the sheet.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const groups = NAV_GROUPS.map((g) => ({
    ...g,
    items: g.items.filter(
      (it) =>
        !(MOBILE_TABS as readonly string[]).includes(it.to) &&
        (!it.minRole || hasMinRole(role, it.minRole)),
    ),
  })).filter((g) => g.items.length > 0);

  if (!open) return null;

  return (
    <div className="lg:hidden fixed inset-0 z-[45]" role="dialog" aria-modal="true" aria-label={t("nav.tabMore")}>
      <button
        type="button"
        aria-label={t("common.close")}
        onClick={onClose}
        className="absolute inset-0 bg-black/40 animate-[fade-in_var(--motion-fast)_var(--ease-out-soft)_both]"
      />

      <div
        className={cn(
          "absolute inset-x-0 bottom-0 max-h-[82dvh] overflow-y-auto",
          "rounded-t-[var(--radius-2xl)] bg-[var(--bg-elevated)]",
          "border-t border-[var(--separator)] shadow-[var(--shadow-pop)]",
        )}
        style={{ paddingBottom: "calc(var(--tabbar-safe-b) + 12px)" }}
      >
        <header className="sticky top-0 bg-[var(--bg-elevated)] flex items-center gap-2 px-4 pt-3 pb-2 border-b border-[var(--separator)]">
          <span aria-hidden className="absolute left-1/2 -translate-x-1/2 top-1.5 h-1 w-9 rounded-full bg-[var(--border-strong)]" />
          <h2 className="mt-1.5 text-[14px] font-semibold text-[var(--fg)]">{t("nav.tabMore")}</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label={t("common.close")}
            className="mt-1.5 ml-auto h-9 w-9 grid place-items-center rounded-[var(--radius-md)] text-[var(--fg-muted)] active:bg-[var(--bg-active)]"
          >
            <X size={18} strokeWidth={1.75} />
          </button>
        </header>

        {groups.map((g) => (
          <section key={g.titleKey || g.items[0]?.to} className="px-2 pt-3">
            {g.titleKey && (
              <h3 className="px-2 pb-1.5 text-[11px] font-semibold uppercase tracking-[0.07em] text-[var(--fg-subtle)]">
                {t(g.titleKey)}
              </h3>
            )}
            <ul className="grid grid-cols-2 gap-1">
              {g.items.map((it) => (
                <li key={it.to}>
                  <NavLink
                    to={it.to}
                    onClick={onClose}
                    className={({ isActive }) =>
                      cn(
                        "flex items-center gap-2.5 px-3 min-h-[48px] rounded-[var(--radius-md)]",
                        "text-[13px] active:bg-[var(--bg-active)]",
                        isActive
                          ? "bg-[var(--bg-subtle)] text-[var(--fg)] font-semibold"
                          : "text-[var(--fg-muted)]",
                      )
                    }
                  >
                    <it.icon
                      size={17}
                      strokeWidth={1.75}
                      className="shrink-0"
                      style={{ color: `var(${g.tone})` }}
                    />
                    <span className="truncate">{t(it.labelKey)}</span>
                  </NavLink>
                </li>
              ))}
            </ul>
          </section>
        ))}

        {/* ── 계정 ──────────────────────────────────────────────
            AccountMenu 는 사이드바 바닥에만 있고 사이드바는 lg 미만에서
            렌더되지 않는다. 이 블록이 없으면 **폰에서 로그아웃할 방법이
            아예 없다** — 공용 단말에서 다음 사람이 세션을 물려받는다.
            /profile 은 NAV_GROUPS 에 없어 위 목록에도 안 뜨므로 여기가
            내 계정으로 가는 유일한 경로이기도 하다. */}
        {user && (
          <section className="mt-3 px-2 pt-3 border-t border-[var(--separator)]">
            <div className="flex items-center gap-3 px-2 pb-2">
              <Avatar className="h-9 w-9 text-[13px] shrink-0">
                <AvatarFallback>
                  {(user.username ?? "?").slice(0, 2).toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <span className="min-w-0 leading-tight">
                <span className="block text-[14px] font-semibold tracking-[-0.01em] text-[var(--fg)] truncate">
                  {user.username}
                </span>
                <span className="block text-[11.5px] text-[var(--fg-subtle)] truncate">
                  {t(ROLE_LABEL_KEY[user.role])}
                  {user.organization?.name ? ` · ${user.organization.name}` : ""}
                </span>
              </span>
            </div>

            <ul className="grid grid-cols-2 gap-1">
              <li>
                <button
                  type="button"
                  onClick={() => {
                    onClose();
                    navigate("/profile");
                  }}
                  className="w-full flex items-center gap-2.5 px-3 min-h-[48px] rounded-[var(--radius-md)] text-[13px] text-[var(--fg-muted)] active:bg-[var(--bg-active)]"
                >
                  <UserIcon size={17} strokeWidth={1.75} className="shrink-0" />
                  <span className="truncate">{t("widgets.account.profile")}</span>
                </button>
              </li>
              <li>
                <button
                  type="button"
                  onClick={() => {
                    onClose();
                    navigate("/change-password");
                  }}
                  className="w-full flex items-center gap-2.5 px-3 min-h-[48px] rounded-[var(--radius-md)] text-[13px] text-[var(--fg-muted)] active:bg-[var(--bg-active)]"
                >
                  <KeyRound size={17} strokeWidth={1.75} className="shrink-0" />
                  <span className="truncate">{t("widgets.account.changePassword")}</span>
                </button>
              </li>
              <li className="col-span-2">
                <button
                  type="button"
                  disabled={logoutM.isPending}
                  onClick={() => {
                    onClose();
                    logoutM.mutate();
                  }}
                  className="w-full flex items-center gap-2.5 px-3 min-h-[48px] rounded-[var(--radius-md)] text-[13px] font-medium text-[var(--danger)] active:bg-[var(--bg-active)] disabled:opacity-50"
                >
                  <LogOut size={17} strokeWidth={1.75} className="shrink-0" />
                  <span className="truncate">{t("widgets.account.logout")}</span>
                </button>
              </li>
            </ul>

            {/* 버전·빌드 — 사이드바 바닥과 같은 정보. 현장에서 "무슨 버전
                쓰고 있냐" 를 물었을 때 폰에서도 답할 수 있어야 한다. */}
            <div className="px-3 pt-2 text-[10.5px] tabular-nums text-[var(--fg-subtle)] truncate">
              v{env.version} · {env.build}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
